import React, { useState } from "react";

/**
 * Saque em moeda estrangeira: as contas que recebem EUR e USD, e o que cada uma exige.
 *
 * ── Por que esta tela existe ──
 *
 * Um saque em PIX aceita a chave no próprio pedido. SEPA (EUR) e Wire (USD) NÃO: exigem
 * conta cadastrada antes, e aprovada pelo provedor. Sem este cadastro, não há saque em
 * euro nem em dólar — e nada na tela dizia isso.
 *
 * ── O dólar é o caso caro, e a tela avisa ANTES ──
 *
 * Wire pede routing number (ABA) e o endereço completo do beneficiário: dados que ninguém
 * tem à mão e que nenhuma outra tela deste produto pede. Descobrir isso campo a campo, na
 * recusa, faz preencher tudo de novo. Por isso a exigência aparece antes do formulário.
 *
 * ── Trilho não aprovado não some: aparece dizendo que não está aprovado ──
 *
 * O provedor autoriza por moeda. Esconder a opção faria o cliente procurar um saque que
 * ele não vê; mostrá-la com o estado diz o que falta e a quem pedir.
 */

const FIELDS = {
  EUR: {
    titulo: "Euro (SEPA)",
    resumo: "IBAN, BIC e país do banco.",
    campos: [
      { chave: "bank_name", rotulo: "Banco", dica: "Nome do banco recebedor" },
      { chave: "iban", rotulo: "IBAN", dica: "Pode digitar com espaços" },
      { chave: "bic", rotulo: "BIC / SWIFT", dica: "8 a 11 caracteres" },
      { chave: "country", rotulo: "País do banco", dica: "ISO de 3 letras, ex. DEU" },
      { chave: "beneficiary_name", rotulo: "Nome do beneficiário" },
    ],
    endereco: false,
  },
  USD: {
    titulo: "Dólar (Wire)",
    resumo:
      "Além do banco e da conta, o dólar exige ROUTING NUMBER (ABA) e o ENDEREÇO COMPLETO do beneficiário.",
    campos: [
      { chave: "bank_name", rotulo: "Banco", dica: "Ex. Chase Bank" },
      { chave: "bank_account_number", rotulo: "Número da conta" },
      { chave: "routing_number", rotulo: "Routing number (ABA)", dica: "9 dígitos" },
      { chave: "beneficiary_name", rotulo: "Nome do beneficiário" },
    ],
    // O endereço é exigência do TRILHO, não detalhe: é o que o banco correspondente pede,
    // e o provedor recusa a conta sem ele.
    endereco: true,
  },
};

const ENDERECO = [
  { chave: "street_line_1", rotulo: "Endereço" },
  { chave: "street_line_2", rotulo: "Complemento", opcional: true },
  { chave: "city", rotulo: "Cidade" },
  { chave: "state", rotulo: "Estado" },
  { chave: "postal_code", rotulo: "ZIP / CEP" },
  { chave: "country", rotulo: "País", dica: "ISO de 3 letras, ex. USA" },
];

function Campo({ rotulo, dica, opcional, valor, onChange }) {
  return (
    <div className="fiat__field">
      <label className="fiat__label">
        {rotulo}
        {opcional ? <span className="fiat__opt"> (opcional)</span> : null}
      </label>
      <input className="fiat__input" value={valor || ""} onChange={(e) => onChange(e.target.value)} />
      {dica ? <span className="fiat__hint">{dica}</span> : null}
    </div>
  );
}

export default function FiatAccountsCard({
  currencies = [],
  accounts = [],
  loading,
  onCreate,
}) {
  const [moeda, setMoeda] = useState(null);
  const [dados, setDados] = useState({});
  const [endereco, setEndereco] = useState({});
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);

  // Só EUR e USD: o BRL não passa por aqui, porque o PIX aceita a chave no próprio saque.
  const estrangeiras = currencies.filter((c) => FIELDS[c.currency]);

  const abrir = (currency) => {
    setMoeda(currency);
    setDados({});
    setEndereco({});
    setNome("");
    setErro("");
  };

  async function enviar(event) {
    event.preventDefault();
    setErro("");
    setEnviando(true);
    try {
      const conta =
        moeda === "USD"
          ? { wire: { ...dados, beneficiary_address: endereco } }
          : { sepa: { ...dados } };

      await onCreate(moeda, { account_name: nome || `Conta em ${moeda}`, ...conta });
      setMoeda(null);
    } catch (err) {
      /*
        A frase vem do SERVIDOR e nomeia o campo que falta, em português. Trocá-la por um
        texto genérico deixaria o cliente adivinhando qual dos doze campos de uma conta em
        dólar ele esqueceu — que é exatamente o que este cadastro existe para evitar.
      */
      setErro(
        err?.response?.data?.message ||
          "Não foi possível cadastrar a conta. Confira os campos e tente de novo.",
      );
    } finally {
      setEnviando(false);
    }
  }

  const config = moeda ? FIELDS[moeda] : null;

  return (
    <section className="card fiat">
      <style>{`
        .fiat__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
        .fiat__field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .fiat__label { font-size: 12px; font-weight: bold; color: var(--muted); text-transform: uppercase; }
        .fiat__opt { text-transform: none; font-weight: normal; }
        .fiat__input { border: 1px solid var(--border); border-radius: 4px; min-height: 38px; padding: 0 10px; font-size: 14px; box-sizing: border-box; width: 100%; }
        .fiat__hint { font-size: 11px; color: var(--muted); }
        .fiat__currency { border: 1px solid var(--border); border-radius: 6px; padding: 12px; margin-top: 10px; }
        .fiat__warn { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 6px; padding: 10px; font-size: 13px; margin-top: 8px; }
        .fiat__pending { font-size: 12px; color: #b42318; }
        .fiat__list { margin-top: 8px; font-size: 13px; }
        .fiat__error { color: #b42318; font-size: 13px; font-weight: bold; }
        .fiat__block { border-top: 1px solid var(--border); margin-top: 14px; padding-top: 12px; }
      `}</style>

      <div className="card__title">Saque em moeda estrangeira</div>

      {loading ? (
        <span>Carregando…</span>
      ) : estrangeiras.length === 0 ? (
        <span>
          Nenhuma moeda estrangeira habilitada para esta empresa. Fale com o suporte.
        </span>
      ) : (
        <>
          <p style={{ color: "var(--muted)", fontSize: 13 }}>
            Euro e dólar exigem uma conta de destino cadastrada e aprovada <b>antes</b> do
            saque — diferente do PIX, em que a chave vai no próprio pedido.
          </p>

          {estrangeiras.map((c) => {
            const config = FIELDS[c.currency];
            const doCliente = accounts.filter((a) => a.payment_rail === c.rail);

            return (
              <div key={c.currency} className="fiat__currency">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <strong>{config.titulo}</strong>
                  {/*
                    Trilho não aprovado APARECE, dizendo que não está. Some da lista é o
                    cliente não entender por que a opção não existe.
                  */}
                  {c.approved ? null : (
                    <span className="fiat__pending">
                      não aprovado para esta empresa — fale com o suporte
                    </span>
                  )}
                </div>

                <div className="fiat__warn">{config.resumo}</div>

                <div className="fiat__list">
                  {doCliente.length === 0 ? (
                    <span style={{ color: "var(--muted)" }}>Nenhuma conta cadastrada.</span>
                  ) : (
                    doCliente.map((a) => (
                      <div key={a.id}>
                        {a.account_name || a.id}
                        {" — "}
                        {/*
                          O status importa: conta em análise NÃO recebe saque, e o provedor
                          recusaria depois. Dizer aqui evita a tentativa.
                        */}
                        {a.status === "approved" ? "aprovada" : `${a.status || "em análise"}`}
                      </div>
                    ))
                  )}
                </div>

                <button
                  type="button"
                  style={{ marginTop: 8 }}
                  onClick={() => abrir(c.currency)}
                  disabled={!c.approved}
                >
                  Cadastrar conta em {c.currency}
                </button>
              </div>
            );
          })}
        </>
      )}

      {config ? (
        <form className="fiat__block" onSubmit={enviar}>
          <h4 style={{ marginTop: 0 }}>Nova conta — {config.titulo}</h4>

          <div className="fiat__grid">
            <Campo rotulo="Nome desta conta" dica="Só para você identificar" valor={nome} onChange={setNome} />
            {config.campos.map((campo) => (
              <Campo
                key={campo.chave}
                rotulo={campo.rotulo}
                dica={campo.dica}
                valor={dados[campo.chave]}
                onChange={(v) => setDados((p) => ({ ...p, [campo.chave]: v }))}
              />
            ))}
          </div>

          {config.endereco ? (
            <div className="fiat__block">
              <h4>Endereço do beneficiário</h4>
              <p style={{ color: "var(--muted)", fontSize: 12, marginTop: 0 }}>
                Exigido pelo banco correspondente. Sem ele a conta é recusada.
              </p>
              <div className="fiat__grid">
                {ENDERECO.map((campo) => (
                  <Campo
                    key={campo.chave}
                    rotulo={campo.rotulo}
                    dica={campo.dica}
                    opcional={campo.opcional}
                    valor={endereco[campo.chave]}
                    onChange={(v) => setEndereco((p) => ({ ...p, [campo.chave]: v }))}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {erro ? <p className="fiat__error">{erro}</p> : null}

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button type="submit" className="btnSaveAndContinue" disabled={enviando}>
              {enviando ? "Enviando..." : "Cadastrar conta"}
            </button>
            <button type="button" onClick={() => setMoeda(null)} disabled={enviando}>
              Cancelar
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
